import { MosaicTrafficLightsValue } from "../../traffic-lights/MosaicTrafficLightsValue";
import { AitRowGroupData } from "./AitRowGroupData";
import {
  AitHeaderGroupData,
  AitHeaderRowData,
  AitLocationBodyRow,
  AitLocationBodyRowGroup,
  AitLocationHeader,
  AitLocationHeaderRow,
  AitTablePosition,
} from "./interface";
import { newCell, newHeaderCell } from "./newCell";
import { newHeaderRow, newRow } from "./newRow";

/**
 * Insert a new row into the header or a body row group.
 *
 * Depending on the `location.position`, this function will either insert a
 * header row or add a new row into a body row group. New cells are created
 * using the provided `defaultCellWidth` and `blankT`. The new row will be
 * inserted at `rowIndex + 1` (or at index 0 when header or row group insert uses
 * `rowIndex` -1). Note: `rowIndex` is determined by finding the index of the row
 * with `aitid` matching `location.rowId`.
 *
 * @template T - The cell payload type
 * @param headerData - Header group data if present, otherwise `false`
 * @param bodyData - Array of body row groups
 * @param location - Target location (header/body row/group) where the row will be inserted
 * @param newLength - Desired minimum length of the rows array after insertion; if current length is >= newLength no change is made
 * @param defaultCellWidth - Default width to use when creating new cells
 * @param blankT - Blank payload value used to initialise new cells
 * @returns Object with `headerData` and/or `bodyData` when a row was added, or an empty object when no insertion occurred
 */
export const trAddRow = <T>(
  headerData: AitHeaderGroupData<T> | false,
  bodyData: AitRowGroupData<T>[],
  location: AitLocationHeader | AitLocationHeaderRow | AitLocationBodyRowGroup | AitLocationBodyRow,
  newLength: number,
  defaultCellWidth: number,
  blankT: T,
): { headerData?: AitHeaderGroupData<T> | false; bodyData?: AitRowGroupData<T>[] } => {
  // Add row to header
  if (
    (location.position === AitTablePosition.header ||
      location.position === AitTablePosition.headerRow) &&
    headerData !== false &&
    headerData.rows.length < newLength
  ) {
    // For header insertion prepend (rowIndex -1); for row-level locate by rowId
    const rowIndex =
      location.position === AitTablePosition.header
        ? -1
        : headerData.rows.findIndex((row) => row.aitid === location.rowId);
    const newrs = [...headerData.rows];
    const newr: AitHeaderRowData<T> = newHeaderRow(defaultCellWidth, blankT, 0, []);
    const cols = headerData.rows[0].cells.map((c) => c.colSpan).reduce((sum, a) => sum + a, 0);
    for (let ci = 0; ci < cols; ci++) {
      // Create new cell, use column width and columnId from row 0
      const c = newHeaderCell<T>(
        headerData.rows[0].cells[ci].colWidth ??
          // istanbul ignore next
          defaultCellWidth,
        blankT,
        headerData.rows[0].cells[ci].columnId,
      );
      // Check rowSpans on previous row
      if (rowIndex >= 0 && newrs[rowIndex].cells[ci].rowSpan !== 1) {
        let riUp = 0;
        while (riUp <= rowIndex && newrs[rowIndex - riUp].cells[ci].rowSpan === 0) riUp++;
        newrs[rowIndex - riUp].cells[ci].rowSpan = newrs[rowIndex - riUp].cells[ci].rowSpan + 1;
        c.rowSpan = 0;
      }
      newr.cells.push(c);
    }
    newr.coreTrafficLight = "ChangedUnknown" as MosaicTrafficLightsValue;
    newrs.splice(rowIndex + 1, 0, newr);
    return { headerData: { ...headerData, rows: newrs, coreTrafficLight: "ChangedUnknown" } };
  } else if (
    (location.position === AitTablePosition.bodyRowGroup ||
      location.position === AitTablePosition.bodyRow) &&
    bodyData[location.rowGroup].rows.length < newLength
  ) {
    // Add row to body row group
    const newBodyData = [...bodyData];
    const newRowGroup = newBodyData[location.rowGroup];
    // For group-level insertion prepend (rowIndex -1); for row-level locate by rowId
    const rowIndex =
      location.position === AitTablePosition.bodyRowGroup
        ? -1
        : newRowGroup.rows.findIndex((row) => row.aitid === location.rowId);
    // Add row to body
    const newrs = [...newRowGroup.rows];
    const newr = newRow<T>(defaultCellWidth, blankT, 0, []);
    const cols = newRowGroup.rows[0].cells.length;
    for (let ci = 0; ci < cols; ci++) {
      // Create new cell, use column width and columnId from row 0
      const c = newCell<T>(
        newRowGroup.rows[0].cells[ci].colWidth ??
          // istanbul ignore next
          defaultCellWidth,
        blankT,
        newRowGroup.rows[0].cells[ci].columnId,
      );
      newr.cells.push(c);
    }
    newr.coreTrafficLight = "ChangedUnknown" as MosaicTrafficLightsValue;
    newrs.splice(rowIndex + 1, 0, newr);
    newBodyData[location.rowGroup] = {
      ...newRowGroup,
      rows: newrs,
      coreTrafficLight: "ChangedUnknown" as MosaicTrafficLightsValue,
    };
    return { bodyData: newBodyData };
  }
  return {};
};
